/**
 * Shared iCalendar (.ics) generator — RFC 5545
 * Works on iOS (opens Calendar), Android and desktop (downloads .ics).
 */

import { stripHtmlToText } from '@/lib/text';

export interface IcsEventInput {
  uid: string;
  title: string;
  startDate: string; // ISO 8601
  endDate?: string | null; // ISO 8601, defaults to startDate + 2h
  description?: string | null; // HTML or plain text
  location?: string | null;
}

/** Format a Date to ICS DTSTART/DTEND value (UTC): 20250615T140000Z */
function formatIcsDate(d: Date): string {
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/** Escape text per RFC 5545 §3.3.11 */
function escIcs(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/** Fold lines longer than 75 octets per RFC 5545 §3.1 */
function foldLine(line: string): string {
  const maxLen = 75;
  if (line.length <= maxLen) return line;
  const parts: string[] = [line.slice(0, maxLen)];
  let pos = maxLen;
  while (pos < line.length) {
    parts.push(' ' + line.slice(pos, pos + maxLen - 1));
    pos += maxLen - 1;
  }
  return parts.join('\r\n');
}

export function buildEventIcs(input: IcsEventInput): string {
  const start = new Date(input.startDate);
  const end = input.endDate
    ? new Date(input.endDate)
    : new Date(start.getTime() + 2 * 60 * 60 * 1000); // default +2h

  const now = new Date();

  // Strip HTML from description if present
  const plainDesc = input.description
    ? stripHtmlToText(input.description)
    : undefined;

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Farmly//Event//SV',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${input.uid}`,
    `DTSTAMP:${formatIcsDate(now)}`,
    `DTSTART:${formatIcsDate(start)}`,
    `DTEND:${formatIcsDate(end)}`,
    foldLine(`SUMMARY:${escIcs(input.title)}`),
  ];

  if (plainDesc) {
    lines.push(foldLine(`DESCRIPTION:${escIcs(plainDesc)}`));
  }
  if (input.location) {
    lines.push(foldLine(`LOCATION:${escIcs(input.location)}`));
  }

  lines.push('END:VEVENT', 'END:VCALENDAR');
  return lines.join('\r\n');
}

export function triggerIcsDownload(filenameBase: string, ics: string) {
  const safeName = filenameBase.replace(/[^a-zåäöA-ZÅÄÖ0-9 ]/g, '').trim() || 'event';
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeName}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
